import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import SectionSlideshow from '../components/Admin/Home/SectionSlideshow/SectionSlideshow';
import SectionDepoimentos from '../components/Admin/Home/SectionDepoimentos/SectionDepoimentos';
import SectionIndicadores from '../components/Admin/Home/SectionIndicadores/SectionIndicadores';
import SectionParceiros from '../components/Admin/Home/SectionParceiros/SectionParceiros';
import SectionVideo from '../components/Admin/Home/SectionVideo/SectionVideo';
import ModalMessage from '../components/Admin/Modal/ModalMessage';
import ModalConfirm from '../components/Admin/Modal/ModalConfirm';
import FormActions from '../components/Admin/FormActions/FormActions';
import { getImageUrl } from '../utils/media';
import './AdminHome.css';

function normalizeHomeSlides(slideshow) {
    return Array.isArray(slideshow)
        ? slideshow.filter((slide) => getImageUrl(slide?.imageUrl || slide?.url || slide))
        : [];
}

const AdminHome = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);

    const [slides, setSlides] = useState([]);
    const [fraseInstitucional, setFraseInstitucional] = useState('Transformando vidas e impactando gerações');
    const [fraseDestaque, setFraseDestaque] = useState('');
    const [linkConheca, setLinkConheca] = useState('/quem-somos');
    const [videoUrl, setVideoUrl] = useState('');
    const [videoVisible, setVideoVisible] = useState(true);
    const [depoimentos, setDepoimentos] = useState([]);
    const [tituloIndicadores, setTituloIndicadores] = useState('Nossos indicadores');
    const [imagemIndicadores, setImagemIndicadores] = useState('');
    const [indicadores, setIndicadores] = useState([]);
    const [parceiros, setParceiros] = useState([]);
    const [isModalCancelarOpen, setIsModalCancelarOpen] = useState(false);
    const [isModalMessageOpen, setIsModalMessageOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [maintenanceSaving, setMaintenanceSaving] = useState(false);

    // Carrega os dados iniciais da Home via API
    const carregarDadosHome = useCallback(async () => {
        try {
            const response = await api.get('/config/home', {
                params: { _ts: Date.now() }
            });
            const data = response.data;

            if (data) {
                setSlides(normalizeHomeSlides(data.slideshow));
                setFraseDestaque(data.highlightPhrase?.text ?? 'Conheça o 7 Gerações!');
                setLinkConheca(data.highlightPhrase?.link || '/quem-somos');
                setFraseInstitucional(data.institutionalPhrase ?? 'Transformando vidas e impactando gerações');
                setVideoUrl(data.institutionalVideo?.url || '');
                setVideoVisible(data.institutionalVideo?.visible !== false);
                setDepoimentos(data.testimonials || []);
                setTituloIndicadores(data.indicatorsSection?.title || 'Nossos indicadores');
                setImagemIndicadores(data.indicatorsSection?.imageUrl || '');
                setIndicadores(data.indicators || []);
                setParceiros(data.partners || []);
            }
        } catch (error) {
            console.error("Erro ao carregar dados da Home:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        carregarDadosHome();
    }, [carregarDadosHome]);

    useEffect(() => {
        api.get('/config/maintenance')
            .then((response) => setMaintenanceMode(response.data?.enabled === true))
            .catch(() => undefined);
    }, []);

    const handleMaintenanceChange = async (event) => {
        const enabled = event.target.checked;
        setMaintenanceMode(enabled);
        setMaintenanceSaving(true);

        try {
            const response = await api.put('/config/maintenance', { enabled });
            setMaintenanceMode(response.data?.enabled === true);
            setModalMessage(enabled ? 'Modo manutenção ativado.' : 'Modo manutenção desativado.');
            setIsModalMessageOpen(true);
        } catch (error) {
            setMaintenanceMode(!enabled);
            setModalMessage(error.response?.data?.error || 'Não foi possível atualizar o modo manutenção.');
            setIsModalMessageOpen(true);
        } finally {
            setMaintenanceSaving(false);
        }
    };

    // Previne o fechamento acidental da aba se houver edições ativas
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isEditing) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isEditing]);

    // Processa o upload de novos arquivos para o servidor/Cloudinary
    const uploadImageIfNeeded = async (itemWithFile) => {
        if (!itemWithFile || !itemWithFile.file) {
            return typeof itemWithFile === 'string'
                ? itemWithFile
                : (itemWithFile?.url || itemWithFile?.imageUrl || itemWithFile?.photoUrl || itemWithFile?.logoUrl || '');
        }

        const formData = new FormData();
        formData.append('image', itemWithFile.file);

        try {
            const response = await api.post('/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data.url;
        } catch (error) {
            console.error("Erro no upload da imagem:", error);
            throw new Error("Falha ao enviar uma das imagens.");
        }
    };

    // Faz o upload em lote e a submissão final dos dados
    const handleSalvar = async (e) => {
        e.preventDefault();
        try {
            const requiredValues = [linkConheca];

            if (requiredValues.some((value) => !String(value || '').trim())) {
                setModalMessage("Preencha todos os campos obrigatórios antes de salvar.");
                setIsModalMessageOpen(true);
                return;
            }

            setLoading(true);

            const slidesToSave = normalizeHomeSlides(slides);
            const slideshowAtualizado = await Promise.all(
                slidesToSave.map(async (slide, index) => {
                    const imageUrl = await uploadImageIfNeeded(slide);
                    return { imageUrl, order: index };
                })
            );

            let imagemIndicadoresUrl = imagemIndicadores;
            if (typeof imagemIndicadores === 'object' && imagemIndicadores?.file) {
                imagemIndicadoresUrl = await uploadImageIfNeeded(imagemIndicadores);
            } else if (typeof imagemIndicadores === 'object') {
                imagemIndicadoresUrl = imagemIndicadores?.url || '';
            }

            const testimonialsAtualizados = await Promise.all(
                depoimentos.map(async (dep, index) => {
                    let photoUrl = dep.photoUrl;
                    if (dep.file) {
                        photoUrl = await uploadImageIfNeeded(dep);
                    }
                    return { ...dep, photoUrl, order: index };
                })
            );

            const partnersAtualizados = await Promise.all(
                parceiros.map(async (parc, index) => {
                    let logoUrl = parc.logoUrl;
                    if (parc.file) {
                        logoUrl = await uploadImageIfNeeded(parc);
                    }
                    return { ...parc, logoUrl, order: index };
                })
            );

            const payload = {
                institutionalVideo: {
                    url: videoUrl.trim(),
                    visible: videoVisible,
                },
                homePage: {
                    slideshow: slideshowAtualizado,
                    highlightPhrase: {
                        text: fraseDestaque.trim(),
                        link: linkConheca.trim()
                    },
                    institutionalPhrase: fraseInstitucional.trim(),
                    institutionalVideo: {
                        url: videoUrl.trim(),
                        visible: videoVisible,
                        platform: videoUrl.includes('instagram') ? 'instagram' : 'youtube'
                    },
                    testimonialsSection: { title: "Depoimentos" },
                    testimonialCategories: ["atendidos", "familiares", "colaboradores", "parceiros"],
                    testimonials: testimonialsAtualizados,
                    indicatorsSection: {
                        title: tituloIndicadores,
                        imageUrl: imagemIndicadoresUrl,
                        description: ""
                    },
                    indicators: indicadores.map((ind, index) => ({
                        value: Number(ind.value) || 0,
                        description: ind.description || ind.label || '',
                        order: index
                    })),
                    partnersSection: { title: "Parceiros" },
                    partnerCategories: ["parceiros-institucionais", "parceiros-em-projetos", "patrocinio", "apoiadores"],
                    partners: partnersAtualizados
                }
            };

            await api.put('/config/home', payload);
            setIsEditing(false);
            setModalMessage('Alterações salvas com sucesso!');
            setIsModalMessageOpen(true);
        } catch (error) {
            console.error("Erro ao salvar dados da Home:", error);
            setModalMessage(error.response?.data?.error || "Erro ao salvar as alterações.");
            setIsModalMessageOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelar = () => {
        setIsModalCancelarOpen(true);
    };

    const handleConfirmCancelar = () => {
        setIsModalCancelarOpen(false);
        carregarDadosHome();
    };

    if (loading && slides.length === 0) {
        return (
            <main className="main-content admin-home-main">
                <div className="admin-loading">Carregando dados da Home...</div>
            </main>
        );
    }

    return (
        <>
            <main className="main-content admin-home-main">
                <div className="admin-home-content">
                    <section className="admin-maintenance-panel" aria-labelledby="maintenance-title">
                        <div>
                            <p className="admin-maintenance-panel__eyebrow">Acesso público</p>
                            <h2 id="maintenance-title">Modo manutenção</h2>
                            <p>Oculta o site para visitantes e mantém o acesso administrativo disponível.</p>
                        </div>
                        <label className="admin-maintenance-toggle">
                            <input
                                type="checkbox"
                                checked={maintenanceMode}
                                onChange={handleMaintenanceChange}
                                disabled={maintenanceSaving}
                            />
                            <span>{maintenanceMode ? 'Ativo' : 'Desativado'}</span>
                        </label>
                    </section>
                    <form className="admin-home-form" onSubmit={handleSalvar}>
                        <SectionSlideshow
                            isEditing={isEditing}
                            slides={slides}
                            setSlides={setSlides}
                            fraseDestaque={fraseDestaque}
                            setFraseDestaque={setFraseDestaque}
                            linkConheca={linkConheca}
                            setLinkConheca={setLinkConheca}
                        />
                    
                        <SectionDepoimentos
                            isEditing={isEditing}
                            depoimentos={depoimentos}
                            setDepoimentos={setDepoimentos}
                            fraseInstitucional={fraseInstitucional}
                            setFraseInstitucional={setFraseInstitucional}
                        />

                        <SectionVideo
                            isEditing={isEditing}
                            videoUrl={videoUrl}
                            setVideoUrl={setVideoUrl}
                            videoVisible={videoVisible}
                            setVideoVisible={setVideoVisible}
                        />

                        <SectionIndicadores
                            isEditing={isEditing}
                            tituloIndicadores={tituloIndicadores}
                            setTituloIndicadores={setTituloIndicadores}
                            imagemIndicadores={imagemIndicadores}
                            setImagemIndicadores={setImagemIndicadores}
                            indicadores={indicadores}
                            setIndicadores={setIndicadores}
                        />

                        <SectionParceiros
                            isEditing={isEditing}
                            parceiros={parceiros}
                            setParceiros={setParceiros}
                        />

                        {/* Ações de Formulário */}
                        <FormActions
                            editing={isEditing}
                            onEdit={() => setIsEditing(true)}
                            onCancel={handleCancelar}
                            saveType="submit"
                            saving={loading}
                        />
                    </form>
                </div>
        </main>

            <ModalConfirm
                isOpen={isModalCancelarOpen}
                onClose={() => setIsModalCancelarOpen(false)}
                onConfirm={handleConfirmCancelar}
                message="Deseja descartar as alterações não salvas?"
            />

            <ModalMessage
                isOpen={isModalMessageOpen}
                onClose={() => setIsModalMessageOpen(false)}
                message={modalMessage}
            />
        </>
    );
};

export default AdminHome;
